import React, { useState } from "react";
import { Box, Button, CircularProgress, DialogActions, DialogContent, Menu, MenuItem, Typography } from "@mui/material";
import { financeApi } from "../../api/config";
import { getStore } from "../../store/store";
import { useUser } from "../../api/useAuth";
import { ROLES } from "../../utils/constants";
import { MonthEndAllocationDialog } from "./components/MonthEndAllocationDialog";
import { DateActionDialog } from "./components/DateActionDialog";

const ADMIN_PERMISSION_ERROR = "Não tem permissões para executar esta ação. Apenas administradores podem utilizar este recurso.";

const isAdmin = (user: any) => {
    return user?.role?.id === ROLES.admin;
};

const showUnauthorizedNotification = () => {
    const { notifications } = getStore();
    notifications.setNotification({
        isOpen: true,
        message: ADMIN_PERMISSION_ERROR,
        severity: "error",
    });
};

export const showResponseDialog = (title: string, responseData: any) => {
    const { appDialog } = getStore();
    const formattedJson = JSON.stringify(responseData, null, 2);

    appDialog.createDialog({
        title,
        contentAndActions: React.createElement(
            React.Fragment,
            null,
            React.createElement(
                DialogContent,
                null,
                React.createElement(
                    Box,
                    {
                        component: "pre",
                        sx: {
                            backgroundColor: "#f5f5f5",
                            p: 2,
                            borderRadius: 1,
                            overflow: "auto",
                            maxHeight: "60vh",
                            fontSize: "0.85rem",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                        },
                    },
                    formattedJson
                )
            ),
            React.createElement(
                DialogActions,
                null,
                React.createElement(Button, { onClick: () => appDialog.toggleIsOpen() }, "Fechar")
            )
        ),
        allowSoftCancel: true,
        maxWidth: "md",
    });
};

const executeSimpleAction = async (endpoint: string, confirmMessage: string, actionName: string) => {
    const confirmed = window.confirm(confirmMessage);
    if (!confirmed) return;

    const { notifications } = getStore();

    try {
        const response = await financeApi.post(`/ai-finance-endpoint/${endpoint}`);

        showResponseDialog(actionName, response.data);
    } catch (error: any) {
        notifications.setNotification({
            isOpen: true,
            message: error?.response?.data?.error || "Ocorreu um erro ao executar a ação",
            severity: "error",
        });
    }
};

const openSyncTransactionsDialog = () => {
    const { appDialog } = getStore();

    appDialog.createDialog({
        title: "Sincronizar Transações",
        contentAndActions: React.createElement(DateActionDialog, {
            onClose: () => appDialog.toggleIsOpen(),
            endpoint: "sync-transactions",
            title: "Sincronizar Transações",
            description: "Selecione uma data de início opcional. Se não for selecionada, será utilizada a data predefinida.",
            dateFields: [{ key: "dateFrom", label: "Data de início (opcional)" }],
        }),
        allowSoftCancel: true,
    });
};

const openMonthEndAllocationDialog = () => {
    const { appDialog } = getStore();

    appDialog.createDialog({
        title: "Alocação de Fim de Mês",
        contentAndActions: React.createElement(MonthEndAllocationDialog, {
            onClose: () => appDialog.toggleIsOpen(),
        }),
        allowSoftCancel: true,
    });
};

const openIfthenMatchingDialog = () => {
    const { appDialog } = getStore();

    appDialog.createDialog({
        title: "Ifthen Matching",
        contentAndActions: React.createElement(DateActionDialog, {
            onClose: () => appDialog.toggleIsOpen(),
            endpoint: "run-ifthen-matching",
            title: "Ifthen Matching",
            description: "Selecione datas opcionais para filtrar o matching. Se não forem selecionadas, serão utilizados os valores predefinidos.",
            dateFields: [
                { key: "dateFrom", label: "A partir da data (opcional" },
                { key: "date", label: "Data específica (opcional)" },
            ],
        }),
        allowSoftCancel: true,
    });
};

const openPosMatchingDialog = () => {
    const { appDialog } = getStore();

    appDialog.createDialog({
        title: "POS Matching",
        contentAndActions: React.createElement(DateActionDialog, {
            onClose: () => appDialog.toggleIsOpen(),
            endpoint: "run-pos-matching",
            title: "POS Matching",
            description: "Selecione uma data de início opcional. Se não for selecionada, será utilizada a data predefinida.",
            dateFields: [{ key: "dateFrom", label: "A partir da data (opcional)" }],
        }),
        allowSoftCancel: true,
    });
};

type ActionDefinition = {
    code: string;
    name: string;
    handler: () => void | Promise<void>;
};

const actionDefinitions: Array<ActionDefinition> = [
    {
        code: "sync_transactions",
        name: "Sincronizar Transações",
        handler: openSyncTransactionsDialog,
    },
    {
        code: "on_demand_categorization",
        name: "Categorizar",
        handler: () =>
            executeSimpleAction(
                "run-on-demand-categorization",
                "Tem a certeza que pretende executar a categorização a pedido?",
                "Categorização a Pedido"
            ),
    },
    {
        code: "immediate_allocation",
        name: "Alocação Imediata",
        handler: () =>
            executeSimpleAction(
                "run-immediate-allocation",
                "Tem a certeza que pretende executar a alocação imediata?",
                "Alocação Imediata"
            ),
    },
    {
        code: "ifthen_matching",
        name: "Acertar IFTHEN",
        handler: openIfthenMatchingDialog,
    },
    {
        code: "pos_matching",
        name: "Acertar POS",
        handler: openPosMatchingDialog,
    },
    {
        code: "month_end_allocation",
        name: "Alocação de Fim de Mês",
        handler: openMonthEndAllocationDialog,
    },
    {
        code: "exception_monitor",
        name: "Encontrar Exceções",
        handler: () =>
            executeSimpleAction(
                "run-exception-monitor",
                "Tem a certeza que pretende executar o monitor de exceções?",
                "Monitor de Exceções"
            ),
    },
];

export const BankTransactionsActionsMenu = () => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [isLoading, setIsLoading] = useState(false);
    const isMenuOpen = Boolean(anchorEl);
    const { data: user } = useUser();

    const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    const handleActionClick = async (action: ActionDefinition) => {
        handleCloseMenu();

        if (!isAdmin(user)) {
            showUnauthorizedNotification();
            return;
        }

        setIsLoading(true);

        try {
            await action.handler();
        } catch (error: any) {
            const { notifications } = getStore();
            notifications.setNotification({
                isOpen: true,
                message: error?.message || "Ocorreu um erro ao executar a ação",
                severity: "error",
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <Button
                color="secondary"
                size="large"
                onClick={handleOpenMenu}
                endIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : undefined}
                disabled={isLoading}
            >
                <Typography variant="body1">Ações</Typography>
            </Button>
            <Menu anchorEl={anchorEl} open={isMenuOpen} onClose={handleCloseMenu}>
                {actionDefinitions.map((action) => (
                    <MenuItem key={action.code} onClick={() => handleActionClick(action)}>
                        {action.name}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};
